import type { MealPlanToShoppingListRequest } from '@recipes/contracts';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@recipes/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '@/features/shared/form-field';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function ShoppingFromPlanDialog({
  open,
  onOpenChange,
  from,
  to,
  householdId,
  onSubmit,
  saving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  from: string;
  to: string;
  householdId: string | null;
  onSubmit: (body: MealPlanToShoppingListRequest) => void;
  saving?: boolean;
}) {
  const { t } = useTranslation();
  const [dateFrom, setDateFrom] = useState(from);
  const [dateTo, setDateTo] = useState(to);
  const [target, setTarget] = useState<'new' | 'existing'>('new');
  const [listName, setListName] = useState('');
  const [listId, setListId] = useState<string>('');
  const [subtractPantry, setSubtractPantry] = useState(false);
  useEffect(() => {
    if (open) {
      setDateFrom(from);
      setDateTo(to);
    }
  }, [open, from, to]);

  const lists = useQuery({
    queryKey: queryKeys.shopping.all,
    queryFn: () => api.shopping.list(),
    enabled: open,
  });
  const active = (lists.data ?? []).filter((l) => l.status === 'ACTIVE');
  const valid = dateFrom && dateTo && dateFrom <= dateTo && (target === 'new' || listId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('mealPlan.shopping.title')}</DialogTitle>
          <DialogDescription>{t('mealPlan.shopping.description')}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            onSubmit({
              from: dateFrom,
              to: dateTo,
              householdId,
              shoppingListId: target === 'existing' ? listId : null,
              listName: target === 'new' ? listName.trim() || undefined : undefined,
              subtractPantry,
            });
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <FormField label={t('mealPlan.shopping.from')} required>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </FormField>
            <FormField label={t('mealPlan.shopping.to')} required>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </FormField>
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('mealPlan.shopping.target')}</legend>
            <label className="flex min-h-[40px] items-center gap-2 text-sm">
              <input
                type="radio"
                name="target"
                className="size-4 accent-primary"
                checked={target === 'new'}
                onChange={() => setTarget('new')}
              />
              {t('mealPlan.shopping.newList')}
            </label>
            {target === 'new' && (
              <FormField
                label={t('mealPlan.shopping.listName')}
                optionalText={t('common.optional')}
              >
                <Input
                  value={listName}
                  maxLength={120}
                  onChange={(e) => setListName(e.target.value)}
                />
              </FormField>
            )}
            <label className="flex min-h-[40px] items-center gap-2 text-sm">
              <input
                type="radio"
                name="target"
                className="size-4 accent-primary"
                checked={target === 'existing'}
                disabled={active.length === 0}
                onChange={() => setTarget('existing')}
              />
              {t('mealPlan.shopping.existingList')}
            </label>
            {target === 'existing' && (
              <div>
                <Label htmlFor="existing-list">{t('mealPlan.shopping.existingList')}</Label>
                <Select value={listId} onValueChange={setListId}>
                  <SelectTrigger id="existing-list" className="mt-1">
                    <SelectValue placeholder="–" />
                  </SelectTrigger>
                  <SelectContent>
                    {active.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </fieldset>
          <label className="flex min-h-[44px] items-center gap-3 text-sm">
            <Checkbox
              checked={subtractPantry}
              onCheckedChange={(c) => setSubtractPantry(c === true)}
            />
            {t('shopping.addRecipe.subtractPantry')}
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!valid}>
              {t('mealPlan.shopping.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
